<template>
    <div :class="{ uppercase: showAllUppercase }">
        <div class="flex justify-end">
            <SpeechRateSlider />
            <button
                type="button"
                class="hover:bg-indigo-200 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                :class="{
                    'text-indigo-700 bg-indigo-100': showAllUppercase,
                    'text-gray-700 bg-gray-100': !showAllUppercase,
                }"
                @click="toggleUppercase"
            >
                <IconLetterCaseToggle class="w-4 h-4" />
            </button>
        </div>

        <div class="" v-if="activity.image_path">
            <div class="mt-1">
                <img :src="activity.image_path" alt="cover" class="h-auto" />
            </div>
        </div>

        <div class="" v-if="activity.accompanying_text">
            <div class="mt-1">
                <p v-html="activity.accompanying_text" class="prose" />
            </div>
        </div>

        <div v-if="activity.materials.length > 0" class="mt-3 md:mt-5">
            <div>
                {{ $tc('messages.materials', 2) }}
            </div>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <ul class="">
                        <li v-for="(row, index) in activity.materials" :key="index" class="list-disc">
                            <span v-if="!row.url">
                                {{ row.title }}
                            </span>
                            <a :href="row.url" target="_blank" class="text-indigo-500" v-else>
                                <svg
                                    class="w-4 h-4 inline-block"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                                    ></path>
                                    <path
                                        d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                                    ></path>
                                </svg>
                                <span class="truncate text-ellipsis overflow-hidden">
                                    {{ row.title ? row.title : row.url }}
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <UnsavedChangesFormWarning :is-dirty="form.isDirty" v-if="form.isDirty" />
        <form :action="meta.links.store" method="post" @submit.prevent="submit" autocomplete="off">
            <div class="md:grid md:grid-cols-2 md:gap-6 mt-5">
                <div
                    v-for="question in form.answers"
                    class="rounded-lg border-2 border-gray-300 py-2 px-3 shadow-lg col-span-2"
                    :key="`question_${question.question_id}`"
                >
                    <div class="flex" v-if="questionsReviewedIncorrect.includes(question.question_id)">
                        <IconEyeExclamation class="text-yellow-600 h-6 w-6" />
                    </div>
                    <div>
                        <component :is="question.type" :question="question" v-model="question.answer" />
                    </div>
                </div>
            </div>

            <div class="flex justify-between items-end mt-5">
                <div>
                    <QuizFormReview
                        :review="options.review"
                        :answers="form.answers"
                        v-if="options.review?.is_reviewable"
                        @handle-review="handleReview"
                    />
                </div>
                <div>
                    <div class="flex justify-end mt-5" v-auto-animate>
                        <AlertMessage v-if="unansweredQuestions.length" class="mt-5 inline-block" type="info">
                            <template v-slot:title>
                                {{ $t('messages.information') }}
                            </template>
                            <template v-slot:body>
                                {{ $t('messages.X_unanswered_questions', { number: unansweredQuestions.length }) }}
                            </template>
                        </AlertMessage>
                    </div>
                    <SubmitFormButton :button-text="$t('messages.send')" :processing="form.processing" class="mt-3" />
                </div>
            </div>
            <TimeAwayCounter @time-away="handleTimeAway" />
        </form>
    </div>
</template>
<script>
import AppButton from '@/components/AppButton.vue';
import HintAlert from '@/components/Quiz/HintAlert.vue';
import SpeakText from '@/components/SpeakText.vue';
import UnsavedChangesFormWarning from '@/components/Forms/UnsavedChangesFormWarning.vue';
import QuestionMultipleChoiceQuiz from '@/components/Quiz/QuestionMultipleChoiceQuiz.vue';
import QuestionShortAnswerQuiz from '@/components/Quiz/QuestionShortAnswerQuiz.vue';
import QuestionOrderPhraseQuiz from '@/components/Quiz/QuestionOrderPhraseQuiz.vue';
import QuestionTrueFalseQuiz from '@/components/Quiz/QuestionTrueFalseQuiz.vue';
import QuestionOrderElementsQuiz from '@/components/Quiz/QuestionOrderElementsQuiz.vue';
import { IconEyeExclamation, IconLetterCaseToggle } from '@tabler/icons-vue';
import { useVoices } from '@/composables/useVoices.js';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import AppWell from '@/Shared/AppWell.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';
import SpeechRateSlider from '@/Pages/Teacher/Quiz/Activity/Preview/partials/SpeechRateSlider.vue';
import QuestionMathFractionQuiz from '@/components/Quiz/QuestionMathFractionQuiz.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import QuestionMathNumberWithToleranceQuiz from '@/components/Quiz/QuestionMathNumberWithToleranceQuiz.vue';
import QuestionMultipleAnswerQuiz from '@/components/Quiz/QuestionMultipleAnswerQuiz.vue';
import QuestionMathXyzAnswersQuiz from '@/components/Quiz/QuestionMathXyzAnswersQuiz.vue';
import QuestionMathMultipleMagnitudesQuiz from '@/components/Quiz/QuestionMathMultipleMagnitudesQuiz.vue';
import QuestionMathPrimeFactorsQuiz from '@/components/Quiz/QuestionMathPrimeFactorsQuiz.vue';
import axios from 'axios';
import QuestionMathPolynomialOperationQuiz from '@/components/Quiz/QuestionMathPolynomialOperationQuiz.vue';
import TimeAwayCounter from './TimeAwayCounter.vue';
import QuizFormReview from './partials/QuizFormReview.vue';
import QuestionMathLatexAnswerQuiz from '@/components/Quiz/QuestionMathLatexAnswerQuiz.vue';
// { question_quiz_import }

export default {
    name: 'QuizForm',
    components: {
        QuizFormReview,
        IconEyeExclamation,
        TimeAwayCounter,
        ZoomableImage,
        SpeechRateSlider,
        QuestionDescriptionDisplay,
        AppWell,
        AlertMessage,
        SubmitFormButton,
        AppButton,
        HintAlert,
        SpeakText,
        UnsavedChangesFormWarning,
        IconLetterCaseToggle,
        'multiple-choice': QuestionMultipleChoiceQuiz,
        'short-answer': QuestionShortAnswerQuiz,
        'order-phrase': QuestionOrderPhraseQuiz,
        'true-false': QuestionTrueFalseQuiz,
        'order-elements': QuestionOrderElementsQuiz,
        'math-fraction': QuestionMathFractionQuiz,
        'math-number-with-tolerance': QuestionMathNumberWithToleranceQuiz,
        'multiple-answer': QuestionMultipleAnswerQuiz,
        'math-xyz-answers': QuestionMathXyzAnswersQuiz,
        'math-multiple-magnitudes': QuestionMathMultipleMagnitudesQuiz,
        'math-prime-factors': QuestionMathPrimeFactorsQuiz,
        'math-polynomial-operation': QuestionMathPolynomialOperationQuiz,
        'math-latex-answer': QuestionMathLatexAnswerQuiz,
        // { question_quiz_component }
    },
    props: {
        activity: {},
        meta: {},
        options: {},
        questions: {},
    },
    setup() {
        const { init } = useVoices();
        init();
    },
    data() {
        return {
            form: this.$inertia.form({
                answers: this.questions,
                adaptation_degree: this.options.adaptation_degree,
                started_at: this.options.started_at,
                time_away: null,
                times_away_count: null,
                reviewed_at: null,
                review_errors_count: null,
            }),
            showAllUppercase: false,
            questionsReviewedIncorrect: [],
        };
    },
    computed: {
        unansweredQuestions: function () {
            return this.form.answers.filter((question) => question.answer === null);
        },
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        toggleUppercase() {
            this.showAllUppercase = !this.showAllUppercase;
            this.logToggleUppercase();
        },
        logToggleUppercase() {
            let toggleActionName = this.showAllUppercase ? 'toggle_quiz_uppercase_on' : 'toggle_quiz_uppercase_off';
            axios.post('/log_action', {
                name: toggleActionName,
            });
        },
        handleTimeAway(timeAway) {
            this.form.time_away += timeAway;
            this.form.times_away_count += 1;
        },
        handleReview(review) {
            this.questionsReviewedIncorrect = review.ids;
            this.form.reviewed_at = review.timestamp;
            this.form.review_errors_count = review.count;
        },
    },
};
</script>
